// Libraries
import { AfterViewInit, Component, effect, EventEmitter, inject, input, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, Signal, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, ThemePalette } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { endOfDay, formatISO, subYears } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Subject, takeUntil } from 'rxjs';

// Services
import { ProjectService } from 'src/app/services/api/projects/project.service';

// Interfaces
import { Project } from 'src/app/interfaces/api/project';

// Classes
import { UiTool } from 'src/app/classes/shared/ui.tools';

// UI
import { ButtonComponent } from 'ui-library';

const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'dd/MM/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd/MM/yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  standalone: true,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: fr },
    { provide: DateAdapter, useClass: DateFnsAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ],
  imports: [
    MatIconModule,
    MatInputModule,
    TranslateModule,
    MatFormFieldModule,
    MatNativeDateModule,
    FormsModule,
    MatDatepickerModule,
    MatButtonModule,
    ReactiveFormsModule,
    NgxMatDatepickerInput,
    NgxMatDatetimepicker,
    ButtonComponent
  ]
})

export class DatePickerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // Services 
  private readonly projectService: ProjectService = inject(ProjectService);
  private _adapter: DateAdapter<any> = inject(DateAdapter);
  private renderer: Renderer2 = inject(Renderer2);
  private readonly uiTool: UiTool = inject(UiTool);

  // Components API
  @Input() startDate: number | string = formatISO(new Date());
  @Input() min: number | string | undefined = "";
  @Input() max: number | string = "";
  @Input() label: string = 'Date';
  @Input() id: string = '';
  @Input() showLabel: boolean = true;
  @Input() showPlaceholder: boolean = false;
  @Input() showSpinners: boolean = true;
  @Input() showSeconds: boolean = false;
  @Input() showMeridian: boolean = false;
  @Input() stepHour: number = 1;
  @Input() stepMinute: number = 1;
  @Input() stepSecond: number = 1;
  @Input() touchUi: boolean = false;
  @Input() color: ThemePalette = 'accent';
  @Input() disabled: boolean = false;
  @Input() disableMinute: boolean = false;
  @Input() enableMeridian: boolean = false;
  @Input() hideTime: boolean = false;
  @Input() formControlName: string = '';

  @Output() dateChanged = new EventEmitter<any>();
  @Output() stateChanged = new EventEmitter<any>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('picker', { static: false }) picker: any;

  // Variables
  dateControl = new FormControl({ value: new Date(this.startDate), disabled: this.disabled });
  name?: string;
  endDate?: string;
  datesColors: any[] = [];
  private unlistenFns: (() => void)[] = [];
  stateTimeout: any;
  projectCreationDate?: any;
  destroy$ = new Subject<void>();
  minDate: Date = subYears(new Date(), 10);
  maxDate: Date = endOfDay(new Date());

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  readonly activity = input<any>(null, { alias: 'activity' });

  constructor() {
    this._adapter.setLocale(fr);

    // Effect: Change project
    effect(() => {
      const project = this.currentProject();

      if (!project?.id) return;

      this.projectCreationDate = project.createdDate;
      this.datesColors = [];
    });

    // Effect: Change activity
    effect(() => {
      const activity = this.activity();

      if (!activity || !activity.details || activity.details.length === 0) {
        this.datesColors = [];
        return;
      }

      this.datesColors = this.uiTool.processActivityToDatesColors(activity);
    });
  }

  /////////////////////////////
  // Angular lifecycle hooks //
  /////////////////////////////

  ngOnInit(): void {
    this.name = "datepicker.replay";
    let dateObj = new Date(this.startDate);

    if (isNaN(dateObj.getTime())) {
      dateObj = new Date();
    }

    const date = dateObj.toISOString();
    this.minDate = this.min ? new Date(this.min) : (this.projectCreationDate || subYears(new Date(), 10));
    this.maxDate = this.max === null ? new Date('2099-12-31') : (this.max ? new Date(this.max) : endOfDay(new Date()));

    this.startDate = date;
    this.endDate = new Date(new Date(this.startDate).getTime() + 23 * 60 * 60 * 1000).toISOString();
  }

  ngAfterViewInit() {
    if (!this.picker) return;

    const openedStream: Subject<any> = this.picker.openedStream;
    const closedStream: Subject<any> = this.picker.closedStream;

    openedStream.asObservable().pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.activity() || !this.datesColors.length) return;

      this.stateTimeout = setTimeout(() => {
        this.checkDates();
        this.uiTool.setupCalendarNavigationListeners(
          this.renderer,
          () => this.checkDates(),
          this.unlistenFns
        );
      }, 400);
    });

    closedStream.asObservable().pipe(takeUntil(this.destroy$)).subscribe(() => {
      clearTimeout(this.stateTimeout);
      this.unlistenFns.forEach(unlisten => unlisten());
      this.unlistenFns = [];
      this.closed.emit();
    });
  }

  ngOnChanges(changes: any): void {
    if (changes['startDate'] && changes['startDate'].currentValue) {
      const newValue = new Date(changes['startDate'].currentValue);
      this.dateControl.setValue(newValue);
    }

    if (changes['min'] || changes['max']) {
      this.minDate = this.min ? new Date(this.min) : (this.projectCreationDate || subYears(new Date(), 10));
      this.maxDate = this.max === null ? new Date('2099-12-31') : (this.max ? new Date(this.max) : endOfDay(new Date()));
    }
  }

  ngOnDestroy() {
    clearTimeout(this.stateTimeout);

    if (this.unlistenFns.length) {
      this.unlistenFns.forEach(unlisten => unlisten());
      this.unlistenFns = [];
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  startDateChange(event: any): void {
    const newDate = new Date(event.value).toISOString();
    this.startDate = newDate;
    this.dateChanged.emit(newDate);
    this.stateChanged.emit({ id: this.id, date: newDate });
  }

  checkDates(): void {
    if (!this.activity() || !this.datesColors.length) return;

    this.uiTool.applyCalendarColors(this.datesColors);
  }
}
